import {ChangeDetectionStrategy, Component, computed, inject, input, OnInit, signal} from '@angular/core';
import {UserService} from "../../services/user.service";
import {FormsModule} from "@angular/forms";
import {UserOverviewDto} from "admin-api-dto";
import {Select, SelectChangeEvent} from "primeng/select";
import {DatePipe} from "@angular/common";
import {PrimeTemplate} from "primeng/api";
import {TableModule} from "primeng/table";
import {Tag} from "primeng/tag";
import {ActivatedRoute, Router} from "@angular/router";
import {TypedTableRowDirective} from "../../typed-table-row.directive";
import {NewWindowLinkComponent} from "../../components/new-window-link/new-window-link.component";
import {InputText} from "primeng/inputtext";
import {Button} from "primeng/button";
import {Tooltip} from "primeng/tooltip";
import {MessageService} from "primeng/api";
import {Subject} from "rxjs";
import {debounceTime, distinctUntilChanged, switchMap, tap} from "rxjs/operators";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

const fuzzinessOptions = [
  {label: 'Exact', value: 0},
  {label: 'Low', value: 0.2},
  {label: 'Medium', value: 0.4},
  {label: 'High', value: 0.6},
] as const;

@Component({
  selector: 'app-better-users-overview',
  imports: [
    FormsModule,
    Select,
    DatePipe,
    PrimeTemplate,
    TableModule,
    Tag,
    TypedTableRowDirective,
    NewWindowLinkComponent,
    InputText,
    Button,
    Tooltip
  ],
  templateUrl: './better-users-overview.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BetterUsersOverviewComponent implements OnInit {
  private readonly userService = inject(UserService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly messageService = inject(MessageService);

  protected readonly users = signal<UserOverviewDto[]>([]);
  protected readonly totalCount = signal(0);
  protected readonly loading = signal(false);

  protected readonly emailSearch = signal('');
  protected readonly emailSearchLoading = signal(false);

  protected readonly phoneSearch = signal('');
  protected readonly phoneSearchLoading = signal(false);

  protected readonly view = input<'all' | 'to-sync' | 'active' | 'mandatory-flow'>('all');
  protected readonly finalView = computed(() => {
    const view = this.view();

    return view ?? 'all';
  });

  protected readonly search = input('');
  protected readonly fuzziness = signal(0.4);
  protected readonly fuzzinessOptions = [...fuzzinessOptions];

  private readonly searchTrigger$ = new Subject<{ search: string; view: string; fuzziness: number }>();

  constructor() {
    this.searchTrigger$.pipe(
      debounceTime(300),
      distinctUntilChanged((a, b) => a.search === b.search && a.view === b.view && a.fuzziness === b.fuzziness),
      tap(() => this.loading.set(true)),
      switchMap(({search, view, fuzziness}) =>
        this.userService.getUsersAfterSignupFlow(search, view, fuzziness)
      ),
      takeUntilDestroyed(),
    ).subscribe({
      next: result => {
        this.users.set(result.users);
        this.totalCount.set(result.totalCount);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
      }
    });
  }

  downloadCsv() {
    this.userService.requestCsvExport().subscribe({
      next: () => window.alert('Export requested. Check your email for the download link.'),
      error: () => window.alert('Failed to request export. Please try again.')
    });
  }

  protected onModeChange(event: SelectChangeEvent) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {view: event.value},
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
    this.triggerSearch(this.search(), event.value, this.fuzziness());
  }

  protected onSearchChange(value: string) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {search: value},
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
    this.triggerSearch(value, this.finalView(), this.fuzziness());
  }

  protected onFuzzinessChange(value: number) {
    this.fuzziness.set(value);
    this.triggerSearch(this.search(), this.finalView(), value);
  }

  public ngOnInit() {
    this.triggerSearch(this.search(), this.finalView(), this.fuzziness());
  }

  private triggerSearch(search: string | undefined, view: string | undefined, fuzziness: number) {
    this.searchTrigger$.next({search: search ?? '', view: view ?? 'all', fuzziness});
  }

  protected readonly sortedUsers = computed(() => this.users());

  protected readonlyViewOptions = [
    {
      label: 'All',
      value: 'all'
    },
    {
      label: 'To be synced soon',
      value: 'to-sync'
    },
    {
      label: 'Active',
      value: 'active'
    },
    {
      label: 'Mandatory Flow',
      value: 'mandatory-flow'
    },
  ] as const;

  openById() {
    const confirmedId = prompt('Enter the user ID to open:');

    if (confirmedId) {
      this.router.navigate(['/users', confirmedId]);
    }
  }

  protected viewOptions = [...this.readonlyViewOptions]

  protected readonly onchange = onchange;

  protected searchByEmail() {
    const email = this.emailSearch().trim();
    if (!email) return;

    this.emailSearchLoading.set(true);
    this.userService.getByEmail(email).subscribe({
      next: users => {
        this.emailSearchLoading.set(false);
        const match = users[0];
        if (match) {
          this.router.navigate(['/users', match.id]);
        } else {
          this.messageService.add({severity: 'warn', summary: 'No user found', detail: `No user with email ${email}`});
        }
      },
      error: () => {
        this.emailSearchLoading.set(false);
      }
    });
  }

  protected searchByPhone() {
    const phone = this.phoneSearch().trim();
    if (!phone) return;

    this.phoneSearchLoading.set(true);
    this.userService.getByPhone(phone).subscribe({
      next: ({userId}) => {
        this.phoneSearchLoading.set(false);
        this.router.navigate(['/users', userId]);
      },
      error: (err) => {
        this.phoneSearchLoading.set(false);
        if (err.status === 404) {
          this.messageService.add({severity: 'warn', summary: 'No user found', detail: `No user with phone number ${phone}`});
        }
      }
    });
  }

}
